import {useEffect, useRef, useState} from "react";
import type {Currency, Lang} from "@zaaaz/shared";
import {FlagIcon, type FlagCode} from "@zaaaz/ui";
import {useI18n} from "./I18nProvider";

const LANGS: {code: Lang; label: string; flag: FlagCode}[] = [
  {code: "ro", label: "RO", flag: "RO"},
  {code: "en", label: "EN", flag: "GB"}
];

const CURRENCIES: {code: Currency; label: string; symbol: string}[] = [
  {code: "ron", label: "RON", symbol: "lei"},
  {code: "eur", label: "EUR", symbol: "€"}
];

type OpenMenu = "lang" | "currency" | null;

export function LocaleSwitcher() {
  const {lang, currency, setLang, setCurrency, t} = useI18n();
  const [open, setOpen] = useState<OpenMenu>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeLang = LANGS.find((l) => l.code === lang) ?? LANGS[0];
  const activeCurrency = CURRENCIES.find((c) => c.code === currency) ?? CURRENCIES[0];
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if(!rootRef.current?.contains(e.target as Node)) setOpen(null);
    }
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape") setOpen(null);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  function toggle(menu: OpenMenu) {
    setOpen((cur) => (cur === menu ? null : menu));
  }
  return (
    <div className="cc-locale" id="langs" ref={rootRef}>
      <div className="cc-locale__pickers">
        <div className={`cc-locale__picker${open === "lang" ? " isOpen" : ""}`}>
          <button
            type="button"
            className="cc-locale__trigger"
            aria-expanded={open === "lang"}
            aria-haspopup="listbox"
            aria-label={t("Language")}
            onClick={() => toggle("lang")}
          >
            <span className="cc-locale__flag" aria-hidden="true">
              <FlagIcon code={activeLang.flag} />
            </span>
            <span className="cc-locale__code">{activeLang.label}</span>
            <i className="fa fa-chevron-down cc-locale__caret" aria-hidden="true" />
          </button>
          {open === "lang" ? (
            <div className="cc-locale__menu" role="listbox" aria-label={t("Language")}>
              {LANGS.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  role="option"
                  aria-selected={lang === item.code}
                  className={`cc-locale__option${lang === item.code ? " isActive" : ""}`}
                  onClick={() => { setLang(item.code); setOpen(null); }}
                >
                  <span className="cc-locale__flag" aria-hidden="true">
                    <FlagIcon code={item.flag} />
                  </span>
                  <span className="cc-locale__optionLabel">{item.label}</span>
                  {lang === item.code ? <i className="fa fa-check cc-locale__check" aria-hidden="true" /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className={`cc-locale__picker${open === "currency" ? " isOpen" : ""}`}>
          <button
            type="button"
            className="cc-locale__trigger"
            aria-expanded={open === "currency"}
            aria-haspopup="listbox"
            aria-label={t("Currency")}
            onClick={() => toggle("currency")}
          >
            <span className="cc-locale__currencySym" aria-hidden="true">{activeCurrency.symbol}</span>
            <span className="cc-locale__code">{activeCurrency.label}</span>
            <i className="fa fa-chevron-down cc-locale__caret" aria-hidden="true" />
          </button>
          {open === "currency" ? (
            <div className="cc-locale__menu" role="listbox" aria-label={t("Currency")}>
              {CURRENCIES.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  role="option"
                  aria-selected={currency === item.code}
                  className={`cc-locale__option${currency === item.code ? " isActive" : ""}`}
                  onClick={() => { setCurrency(item.code); setOpen(null); }}
                >
                  <span className="cc-locale__currencySym" aria-hidden="true">{item.symbol}</span>
                  <span className="cc-locale__optionLabel">{item.label}</span>
                  {currency === item.code ? <i className="fa fa-check cc-locale__check" aria-hidden="true" /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
